import React, { useState } from 'react';
import { api } from '../api';
import { currencyCode } from '../utils/currency';
import { todayISO } from '../utils/format';

// Records a weight or vaccination for every selected sheep in one pass.
export default function BatchModal({ mode, sheep, onClose, onDone }) {
  const isWeight = mode === 'weight';
  const [form, setForm] = useState(
    isWeight
      ? { weight_kg: '', date: todayISO(), weight_type: 'periodic' }
      : { vaccine_type: '', date_given: todayISO(), due_date: '', cost: '', notes: '' }
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (isWeight && !form.weight_kg) return setError('Weight is required.');
    if (!isWeight && !form.vaccine_type.trim()) return setError('Vaccine type is required.');
    setError('');
    setBusy(true);
    const failures = [];
    for (let i = 0; i < sheep.length; i += 1) {
      setProgress({ done: i, total: sheep.length });
      const s = sheep[i];
      try {
        if (isWeight) {
          await api.addWeight(s.id, { ...form, weight_kg: parseFloat(form.weight_kg) });
        } else {
          await api.addVaccination(s.id, {
            vaccine_type: form.vaccine_type,
            date_given: form.date_given,
            due_date: form.due_date || null,
            cost: form.cost !== '' ? parseFloat(form.cost) : null,
            notes: form.notes || null,
          });
        }
      } catch (err) {
        failures.push(`${s.tag_id}: ${err.message}`);
      }
    }
    setBusy(false);
    if (failures.length) {
      setError(`${failures.length} failed. ${failures.slice(0, 3).join('; ')}`);
      return;
    }
    onDone(`${isWeight ? 'Weight' : 'Vaccination'} recorded for ${sheep.length} sheep.`);
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && !busy && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>{isWeight ? 'Batch Weigh-In' : 'Batch Vaccination'}</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose} disabled={busy}>✕</button>
        </div>

        <p className="text-muted" style={{ fontSize: 13 }}>
          Applies to <strong>{sheep.length}</strong> selected sheep: {sheep.slice(0, 6).map(s => s.tag_id).join(', ')}{sheep.length > 6 ? '…' : ''}
        </p>

        <div className="form-grid">
          {isWeight ? (
            <div className="form-grid form-grid-3">
              <div className="field">
                <label>Weight (kg)</label>
                <input type="number" step="0.1" value={form.weight_kg} onChange={e => set('weight_kg', e.target.value)} placeholder="e.g. 45.5" />
              </div>
              <div className="field">
                <label>Date</label>
                <input type="date" value={form.date} onChange={e => set('date', e.target.value)} />
              </div>
              <div className="field">
                <label>Type</label>
                <select value={form.weight_type} onChange={e => set('weight_type', e.target.value)}>
                  <option value="birth">Birth</option>
                  <option value="weaning">Weaning</option>
                  <option value="periodic">Periodic</option>
                </select>
              </div>
            </div>
          ) : (
            <>
              <div className="form-grid form-grid-2">
                <div className="field">
                  <label>Vaccine Type</label>
                  <input value={form.vaccine_type} onChange={e => set('vaccine_type', e.target.value)} placeholder="e.g. Clostridial 7-in-1" />
                </div>
                <div className="field">
                  <label>Cost per head ({currencyCode()})</label>
                  <input type="number" min="0" step="0.01" value={form.cost} onChange={e => set('cost', e.target.value)} placeholder="0.00" />
                </div>
              </div>
              <div className="form-grid form-grid-2">
                <div className="field">
                  <label>Date Given</label>
                  <input type="date" value={form.date_given} onChange={e => set('date_given', e.target.value)} />
                </div>
                <div className="field">
                  <label>Next Due Date</label>
                  <input type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} />
                </div>
              </div>
              <div className="field">
                <label>Notes</label>
                <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional" />
              </div>
            </>
          )}
        </div>

        {error && <div className="alert alert-danger mt-3">{error}</div>}
        {busy && progress && (
          <p className="text-muted mt-2" style={{ fontSize: 13 }}>Saving… {progress.done}/{progress.total}</p>
        )}

        <div className="flex gap-2 mt-4" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : `Apply to ${sheep.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
